import React from 'react';
import {
  Megaphone, CheckCircle, Clock, AlertTriangle, Layers,
  Play, BarChart3, Mail, FlaskConical, Palette, Zap, FileText,
  Users, Shield, GraduationCap, Headphones, Globe, School
} from 'lucide-react';

const UPDATES = [
  {
    version: "3.0.1",
    label: "Demo Mode Cleanup",
    what: "Removed old demo mode from regular exams — unauthenticated visitors now redirect to /try-demo instead of seeing a limited 1-question-per-modality experience. Deleted orphaned DemoRequestForm. Cleaned up GradeSelect and ExamIntegrity",
    why: "Simpler codebase; one clear path for visitors (try-demo) instead of a confusing degraded exam experience",
    icon: Zap,
    color: "bg-teal-100 text-teal-700",
  },
  {
    version: "3.0.1",
    label: "A/B Testing for Landing Page",
    what: "Hero CTA button now runs a live A/B test with 3 copy variants — we'll know which wording drives the most demo completions",
    why: "Data-driven optimization instead of guessing what converts best",
    icon: FlaskConical,
    color: "bg-fuchsia-100 text-fuchsia-700",
  },
  {
    version: "3.0.0",
    label: "Landing Page Redesign",
    what: "Complete Home page overhaul — bold hero, yellow 'Try Free Demo' CTA above the fold, live demo counter, social proof stats bar, trust badges",
    why: "First impression is now conversion-optimized; visitors see value immediately",
    icon: Palette,
    color: "bg-sky-100 text-sky-700",
  },
  {
    version: "2.9.1",
    label: "Lead Scoring",
    what: "Every lead auto-scored 0-100 and tagged Hot/Warm/Cold based on signals (demo completed, decision maker identified, ELL count, grades requested)",
    why: "Sales team knows which leads to call first without reading every submission",
    icon: BarChart3,
    color: "bg-red-100 text-red-700",
  },
  {
    version: "2.9.0",
    label: "Email Drip Sequences",
    what: "3 automated follow-up emails after a demo trial — 48-hour results recap, 5-day social proof, 10-day free pilot offer. Stops automatically when lead converts",
    why: "Leads get nurtured without manual follow-up; top-of-funnel runs on autopilot",
    icon: Mail,
    color: "bg-violet-100 text-violet-700",
  },
  {
    version: "2.8.1",
    label: "Demo Leads Dashboard",
    what: "New 'Leads' tab in Super Admin — unified view of all incoming leads with a 5-stage pipeline (New, Contacted, Scheduled, Won, Lost)",
    why: "Sales pipeline lives inside the platform; no separate CRM needed to start",
    icon: Users,
    color: "bg-amber-100 text-amber-700",
  },
  {
    version: "2.8.0",
    label: "Free Demo Exam ('Try Before You Buy')",
    what: "Visitors pick any grade, take a 12-question mini WIDA exam, see instant results — then prompted to request full access with grade selection and decision maker info",
    why: "Zero-friction product trial that captures qualified leads with the info we need to activate and close",
    icon: Play,
    color: "bg-emerald-100 text-emerald-700",
  },
  {
    version: "2.7.2",
    label: "Request a Demo Form",
    what: "Public form at /request-demo collecting contact info, grade bands, ELL count, preferred date. Auto-emails sales team + sends confirmation to requester",
    why: "Professional lead capture for inbound interest from schools/districts",
    icon: FileText,
    color: "bg-pink-100 text-pink-700",
  },
];

const MILESTONES = [
  { version: "2.7.1", label: "Test-Taking Tips & Parent Guide", summary: "Pre-exam tips by age group; parent guide with 'Assessment Day' section" },
  { version: "2.7.0", label: "Content Overhaul & 3-Tier UI", summary: "441 unique multicultural questions; K-2/3-8/9-12 adaptive interfaces" },
  { version: "2.6.x", label: "Teacher Exam Preview Isolation", summary: "Teachers preview exams without affecting student data or analytics" },
  { version: "2.5.x", label: "Feature Showcase with TTS Voiceovers", summary: "16-slide animated showcase with 'nova' voice narration" },
  { version: "2.0-2.4", label: "Sales & Compliance", summary: "Purchase orders, license tiers, ERMA/FERPA compliance, sales playbook, demo data seeding" },
  { version: "1.x", label: "Core Platform", summary: "Full K-12 WIDA assessment engine, 4 modalities, RBAC, analytics, growth tracking, CSV/PDF, grading, STT/TTS, guided tours" },
];

const BLOCKED = [
  { item: "Azure AD & Clever SSO", reason: "Waiting on Client ID, Tenant ID, and Client Secrets to enable district SSO" },
];

const BACKLOG = [
  "Offline mode / PWA",
  "SOC 2 / FERPA formal certification docs",
  "UTM tracking for marketing channel attribution",
];

const TeamUpdatesTab = () => {
  return (
    <div className="space-y-8" data-testid="team-updates-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Megaphone size={22} className="text-[#002FA7]" />
          <div>
            <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Team Update Notes</h2>
            <p className="text-sm text-gray-500">Quick recap for meetings — what changed and why it matters</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-[#002FA7] text-white text-xs font-bold">v3.0.1</span>
      </div>

      {/* Recent Updates */}
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase mb-4 tracking-wide">Recent Updates</h3>
        <div className="space-y-3">
          {UPDATES.map((u) => {
            const Icon = u.icon;
            return (
              <div key={u.version} className="border-2 border-gray-200 bg-white p-4 hover:border-gray-300 transition-colors" data-testid={`update-${u.version}`}>
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-10 h-10 ${u.color} flex items-center justify-center`}>
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs font-mono font-bold">{u.version}</span>
                      <h4 className="font-bold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{u.label}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-1"><span className="font-semibold text-gray-500">What:</span> {u.what}</p>
                    <p className="text-sm text-[#002FA7]"><span className="font-semibold">Why it matters:</span> {u.why}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Earlier Milestones */}
      <div>
        <h3 className="text-sm font-bold text-gray-400 uppercase mb-4 tracking-wide">Earlier Milestones</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {MILESTONES.map((m) => (
            <div key={m.version} className="border border-gray-200 bg-gray-50 p-3" data-testid={`milestone-${m.version}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-1.5 py-0.5 bg-gray-200 text-gray-500 text-xs font-mono font-bold">{m.version}</span>
                <span className="font-semibold text-sm">{m.label}</span>
              </div>
              <p className="text-xs text-gray-500">{m.summary}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Blocked & Backlog */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border-2 border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-bold text-amber-800 uppercase mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> Blocked
          </h3>
          {BLOCKED.map((b, i) => (
            <div key={i} className="mb-2">
              <p className="font-semibold text-sm text-amber-900">{b.item}</p>
              <p className="text-xs text-amber-700">{b.reason}</p>
            </div>
          ))}
        </div>
        <div className="border-2 border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-bold text-gray-500 uppercase mb-3 flex items-center gap-2">
            <Layers size={14} /> Backlog
          </h3>
          <ul className="space-y-1">
            {BACKLOG.map((b, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-center gap-2">
                <Clock size={12} className="text-gray-400" /> {b}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TeamUpdatesTab;
